import React, { useCallback, useEffect, useState } from "react";
import { Check, Undo2 } from "lucide-react";
import { api, fmtDate } from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const rp = (v) => `Rp ${Number(v || 0).toLocaleString("id-ID")}`;

const Sum = ({ label, value, warna, testid }) => (
  <div className="glass rounded-2xl p-5" data-testid={testid}>
    <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">{label}</p>
    <div className={`mt-2 font-display text-2xl font-light ${warna}`}>{value}</div>
  </div>
);

const HEAD = ["Tanggal", "Kategori", "Jenis", "Merk", "Ukuran", "Qty", "Harga Beli", "Total Beli", "Aksi"];

export const SetorBahanBoard = () => {
  const [range, setRange] = useState({ start: "", end: "" });
  const [belum, setBelum] = useState({ rows: [], jumlah_item: 0, total_beli: 0, total_qty: 0 });
  const [riwayat, setRiwayat] = useState({ rows: [], jumlah_item: 0, total_beli: 0, total_qty: 0 });
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    const params = {
      ...(range.start ? { start: range.start } : {}),
      ...(range.end ? { end: range.end } : {}),
    };
    try {
      const [a, b] = await Promise.all([
        api.get("/setor-bahan", { params }),
        api.get("/setor-bahan/riwayat", { params }),
      ]);
      setBelum(a.data);
      setRiwayat(b.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat data setor bahan");
    }
  }, [range]);

  useEffect(() => {
    load();
  }, [load]);

  const setor = async (r) => {
    setBusy(r.id);
    try {
      await api.post(`/setor-bahan/${r.id}`);
      toast.success(`${r.kategori} · ${r.jenis} ditandai sudah setor`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menandai setor");
    } finally {
      setBusy(null);
    }
  };

  const batal = async (r) => {
    setBusy(r.id);
    try {
      await api.delete(`/setor-bahan/${r.id}`);
      toast.success("Setor dibatalkan");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal membatalkan setor");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="setor-bahan-board">
      <div className="glass rounded-2xl p-4 flex flex-wrap items-end gap-3">
        <div>
          <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Dari Tanggal</span>
          <Input
            data-testid="setor-filter-start"
            type="date"
            value={range.start}
            onChange={(e) => setRange({ ...range, start: e.target.value })}
            className="mt-1 h-10 bg-slate-900/70 border-white/10"
          />
        </div>
        <div>
          <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Sampai</span>
          <Input
            data-testid="setor-filter-end"
            type="date"
            value={range.end}
            onChange={(e) => setRange({ ...range, end: e.target.value })}
            className="mt-1 h-10 bg-slate-900/70 border-white/10"
          />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <Sum testid="setor-sum-item" label="Jumlah Setor (Item Belum Setor)" value={belum.jumlah_item} warna="text-slate-100" />
        <Sum testid="setor-sum-qty" label="Total Qty Belum Setor" value={belum.total_qty} warna="text-amber-300" />
        <Sum testid="setor-sum-nilai" label="Nilai Harga Beli Belum Setor" value={rp(belum.total_beli)} warna="text-rose-300" />
      </div>

      <div className="glass rounded-2xl p-5 md:p-6 space-y-3">
        <h4 className="font-display text-sm font-semibold uppercase tracking-wider text-slate-200">
          Setor Bahan Baku · Belum Disetor
        </h4>
        <p className="text-[10px] text-slate-500">
          Terisi otomatis setiap ada tagihan bahan masuk (mutasi stok keluar bahan).
        </p>
        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-xs">
            <thead className="bg-slate-900/80 text-slate-400 uppercase text-[10px] tracking-wider">
              <tr>
                {HEAD.map((h) => (
                  <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody data-testid="setor-list">
              {belum.rows.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-3 py-8 text-center text-slate-500">
                    Tidak ada bahan yang perlu disetor pada rentang ini.
                  </td>
                </tr>
              )}
              {belum.rows.map((r) => (
                <tr key={r.id} className="border-t border-white/5 hover:bg-slate-800/40">
                  <td className="px-3 py-2 text-slate-400">{fmtDate(r.tanggal)}</td>
                  <td className="px-3 py-2 font-medium text-slate-200">{r.kategori}</td>
                  <td className="px-3 py-2">{r.jenis}</td>
                  <td className="px-3 py-2 text-slate-400">{r.merk || "-"}</td>
                  <td className="px-3 py-2 text-slate-400">{r.ukuran || "-"}</td>
                  <td className="px-3 py-2">{r.qty}</td>
                  <td className="px-3 py-2">{rp(r.harga_beli)}</td>
                  <td className="px-3 py-2 font-semibold text-amber-300">{rp(r.total_beli)}</td>
                  <td className="px-3 py-2">
                    <Button
                      data-testid={`setor-btn-${r.id}`}
                      onClick={() => setor(r)}
                      disabled={busy === r.id}
                      className="h-8 rounded-lg bg-emerald-600 hover:bg-emerald-500 px-3 text-[10px] font-semibold uppercase"
                    >
                      <Check className="w-3.5 h-3.5 mr-1" /> Sudah Setor
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="glass rounded-2xl p-5 md:p-6 space-y-3" data-testid="setor-riwayat-panel">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h4 className="font-display text-sm font-semibold uppercase tracking-wider text-slate-200">
            Rekap Riwayat Sudah Setor
          </h4>
          <div className="flex gap-4 text-[11px] text-slate-400">
            <span data-testid="setor-riwayat-item">Item: <b className="text-slate-200">{riwayat.jumlah_item}</b></span>
            <span data-testid="setor-riwayat-qty">Qty: <b className="text-slate-200">{riwayat.total_qty}</b></span>
            <span data-testid="setor-riwayat-nilai">Nilai: <b className="text-emerald-300">{rp(riwayat.total_beli)}</b></span>
          </div>
        </div>
        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-xs">
            <thead className="bg-slate-900/80 text-slate-400 uppercase text-[10px] tracking-wider">
              <tr>
                {["Tgl Setor", "Tgl Keluar", "Kategori", "Jenis", "Merk", "Ukuran", "Qty", "Total Beli", "Oleh", "Aksi"].map((h) => (
                  <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody data-testid="setor-riwayat-list">
              {riwayat.rows.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-3 py-8 text-center text-slate-500">
                    Belum ada riwayat setor pada rentang ini.
                  </td>
                </tr>
              )}
              {riwayat.rows.map((r) => (
                <tr key={r.id} className="border-t border-white/5 hover:bg-slate-800/40">
                  <td className="px-3 py-2 text-emerald-300">{r.setor_tanggal ? fmtDate(r.setor_tanggal) : "-"}</td>
                  <td className="px-3 py-2 text-slate-400">{fmtDate(r.tanggal)}</td>
                  <td className="px-3 py-2 font-medium text-slate-200">{r.kategori}</td>
                  <td className="px-3 py-2">{r.jenis}</td>
                  <td className="px-3 py-2 text-slate-400">{r.merk || "-"}</td>
                  <td className="px-3 py-2 text-slate-400">{r.ukuran || "-"}</td>
                  <td className="px-3 py-2">{r.qty}</td>
                  <td className="px-3 py-2 font-semibold text-emerald-300">{rp(r.total_beli)}</td>
                  <td className="px-3 py-2 text-slate-400">{r.setor_by || "-"}</td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      data-testid={`setor-cancel-${r.id}`}
                      onClick={() => batal(r)}
                      disabled={busy === r.id}
                      className="rounded-lg border border-amber-400/25 bg-amber-500/10 px-2 py-1 text-[10px] font-semibold uppercase text-amber-300 hover:bg-amber-500/20 disabled:opacity-40"
                    >
                      <Undo2 className="w-3 h-3 mr-1 inline" /> Batal
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
